import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { codingProfile as baselineData } from "../../data/codingProfile";
import {
  Flame,
  Calendar,
  CheckCircle2,
  ExternalLink,
  Code2,
  Layers,
  RotateCw
} from "lucide-react";
import { FaCode } from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";

const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.7; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.7; }
`;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 80px 24px 60px;

  @media (max-width: 768px) {
    padding: 60px 16px 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1140px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  text-align: center;
  max-width: 640px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 14.5px;
    margin-bottom: 18px;
  }
`;

// Live Sync Indicator & Refresh Bar
const LiveSyncBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 24px;
  padding: 10px 18px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
  flex-wrap: wrap;
  gap: 12px;
`;

const LiveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 10px #10b981;
  display: inline-block;
  animation: ${pulse} 2s infinite ease-in-out;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }

  svg.spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Top KPI Cards
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 18px;
  width: 100%;
  margin-bottom: 28px;
`;

const StatCard = styled(motion.div)`
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 18px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.35);
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${({ color }) => `${color}1a`};
  border: 1px solid ${({ color }) => `${color}33`};
  color: ${({ color }) => color};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.2;
`;

const StatLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 2px;
`;

// Main Consistency Card (Replicating TakeUForward UI)
const ConsistencyCard = styled(motion.div)`
  width: 100%;
  background: #0b0f19;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  padding: 30px 32px 26px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  margin-bottom: 28px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 20px 16px;
    border-radius: 18px;
  }
`;

const ConsistencyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 22px;
`;

const ConsistencyTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #f1f5f9;
`;

const HeaderMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13.5px;
  color: #94a3b8;
`;

const PlatformBadge = styled.span`
  font-size: 13px;
  color: #94a3b8;
`;

const DurationBadge = styled.span`
  font-size: 13px;
  color: #e2e8f0;
  font-weight: 600;
  background: #171e2e;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const FilterButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const FilterPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid
    ${({ active }) => (active ? "#3b82f6" : "rgba(255, 255, 255, 0.1)")};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)"
      : "#131b2e"};
  color: ${({ active }) => (active ? "#ffffff" : "#94a3b8")};

  &:hover {
    color: #ffffff;
    border-color: #3b82f6;
  }
`;

// Heatmap Calendar Grid
const HeatmapScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;

  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: #334155;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #38bdf8;
  }
`;

const HeatmapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 780px;
`;

const GridWeeks = styled.div`
  display: grid;
  grid-template-columns: repeat(52, 1fr);
  grid-template-rows: repeat(7, 1fr);
  grid-auto-flow: column;
  gap: 4px;
`;

const Cell = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${({ level }) => {
    switch (level) {
      case 1:
        return "#1e3a8a"; // deep blue
      case 2:
        return "#2563eb"; // medium blue
      case 3:
        return "#38bdf8"; // cyan blue
      case 4:
        return "#60a5fa"; // glowing light blue
      default:
        return "#161e2e"; // dark inactive
    }
  }};
  border: 1px solid
    ${({ level }) => (level > 0 ? "rgba(56, 189, 248, 0.4)" : "rgba(255, 255, 255, 0.04)")};
  box-shadow: ${({ level }) =>
    level >= 2 ? "0 0 6px rgba(56, 189, 248, 0.4)" : "none"};
  transition: transform 0.15s ease, border-color 0.15s ease;
  position: relative;
  cursor: pointer;

  &:hover {
    transform: scale(1.35);
    z-index: 10;
    border-color: #38bdf8;
    box-shadow: 0 0 10px #38bdf8;
  }
`;

const MonthsRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 6px 0;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  min-width: 780px;
`;

// Bottom stats bar
const HeatmapFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const BottomStatRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 24px;
  flex-wrap: wrap;
`;

const SubStat = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;

  span.num {
    font-size: 20px;
    font-weight: 800;
    color: #f8fafc;
  }

  span.label {
    font-size: 12.5px;
    color: #94a3b8;
  }
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
`;

const LegendCell = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${({ bg }) => bg};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

// Detailed Platform Breakdown Cards
const PlatformsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 22px;
  width: 100%;
`;

const PlatformCard = styled.div`
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => `${theme.primary}66`};
    transform: translateY(-2px);
  }
`;

const PlatformHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PlatformIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlatformLogoBox = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${({ color }) => `${color}18`};
  border: 1px solid ${({ color }) => `${color}33`};
  color: ${({ color }) => color};
  display: grid;
  place-items: center;
`;

const PlatformName = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`;

const ExternalProfileLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_secondary};
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
  }
`;

const ProgressBarsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CategoryProgress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CategoryTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 7px;
  border-radius: 999px;
  background: ${({ theme }) => theme.bgSoft};
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${({ color }) => color};
  width: ${({ pct }) => `${pct}%`};
  transition: width 0.6s ease;
`;

const TopTopicsWrapper = styled.div`
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const TopicsHeading = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const TopicChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TopicChip = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_secondary};

  span.cnt {
    color: ${({ theme }) => theme.primary};
    font-weight: 700;
    margin-left: 4px;
  }
`;

const CodingProfile = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Dynamic live stats state (defaults to latest synced baseline data)
  const [liveStats, setLiveStats] = useState({
    totalSolved: baselineData.totalSolved,
    activeDays: baselineData.activeDays,
    bestStreak: baselineData.bestStreak,
    contributions12m: baselineData.contributions12m,
    leetCodeSolved: baselineData.platforms.LeetCode.solved,
    leetCodeEasy: baselineData.platforms.LeetCode.categories[0].solved,
    leetCodeMedium: baselineData.platforms.LeetCode.categories[1].solved,
    leetCodeHard: baselineData.platforms.LeetCode.categories[2].solved,
    tufSolved: baselineData.platforms.TUF.solved,
    ranking: null,
    dynamicHeatmap: baselineData.heatmapData
  });

  // Dynamic fetch function that queries live TakeUForward and LeetCode stats
  const fetchLiveData = useCallback(async () => {
    setIsRefreshing(true);
    let updatedLeetCode = null;
    let updatedTuf = null;

    // 1. Fetch live TakeUForward stats
    try {
      // First try live serverless endpoint /api/tuf (Vercel / Netlify)
      let tufRes = await fetch("/api/tuf");
      if (!tufRes.ok) {
        // Fallback to static public json
        tufRes = await fetch("/tuf-data.json");
      }

      if (tufRes.ok) {
        const tufData = await tufRes.json();
        if (tufData && (tufData.stats || tufData.heatmapData)) {
          updatedTuf = tufData;
        }
      }
    } catch (tufErr) {
      console.warn("TUF dynamic fetch note:", tufErr.message);
    }

    // 2. Fetch live LeetCode stats
    try {
      const lcRes = await fetch("https://leetcode-api-faisalshohag.vercel.app/vicku915");
      if (lcRes.ok) {
        const lcData = await lcRes.json();
        if (lcData && lcData.totalSolved) {
          updatedLeetCode = lcData;
        }
      }
    } catch (lcErr) {
      console.warn("LeetCode dynamic fetch note:", lcErr.message);
    }

    // 3. Merge live statistics from both platforms
    const dateMap = new Map();

    // Start with baseline or updated TUF heatmap
    const baseHeatmap = updatedTuf?.heatmapData || baselineData.heatmapData;
    baseHeatmap.forEach((item) => {
      dateMap.set(item.date, {
        date: item.date,
        count: item.count,
        platform: item.platform || "TUF"
      });
    });

    // Merge live LeetCode submissionCalendar
    if (updatedLeetCode?.submissionCalendar) {
      Object.entries(updatedLeetCode.submissionCalendar).forEach(([ts, count]) => {
        const date = new Date(parseInt(ts, 10) * 1000).toISOString().split("T")[0];
        if (dateMap.has(date)) {
          const existing = dateMap.get(date);
          existing.count = Math.max(existing.count, count);
          existing.platform = "All";
        } else {
          dateMap.set(date, { date, count, platform: "LeetCode" });
        }
      });
    }

    const mergedHeatmap = Array.from(dateMap.values());
    const newActiveDays = updatedTuf?.stats?.activeDays || mergedHeatmap.length;
    const newContributions = updatedTuf?.stats?.totalContributions || mergedHeatmap.reduce((acc, curr) => acc + curr.count, 0);

    const tufCount = updatedTuf?.tufSolved || baselineData.platforms.TUF.solved;
    const lcCount = updatedLeetCode?.totalSolved || baselineData.platforms.LeetCode.solved;
    const lcEasy = updatedLeetCode?.easySolved ?? baselineData.platforms.LeetCode.categories[0].solved;
    const lcMedium = updatedLeetCode?.mediumSolved ?? baselineData.platforms.LeetCode.categories[1].solved;
    const lcHard = updatedLeetCode?.hardSolved ?? baselineData.platforms.LeetCode.categories[2].solved;

    setLiveStats({
      totalSolved: lcCount + tufCount,
      activeDays: Math.max(newActiveDays, baselineData.activeDays),
      bestStreak: updatedTuf?.stats?.bestStreak || baselineData.bestStreak,
      contributions12m: Math.max(newContributions, baselineData.contributions12m),
      leetCodeSolved: lcCount,
      leetCodeEasy: lcEasy,
      leetCodeMedium: lcMedium,
      leetCodeHard: lcHard,
      tufSolved: tufCount,
      ranking: updatedLeetCode?.ranking || null,
      dynamicHeatmap: mergedHeatmap
    });

    setLastSyncTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    fetchLiveData();
  }, [fetchLiveData]);

  // Build the 52 weeks x 7 days grid dynamically
  const gridCells = useMemo(() => {
    const activityMap = new Map();
    liveStats.dynamicHeatmap.forEach((item) => {
      if (activeFilter === "All" || item.platform === activeFilter || item.platform === "All") {
        activityMap.set(item.date, item.count);
      }
    });

    const cells = [];
    const totalDays = 52 * 7;
    const now = new Date();

    for (let i = totalDays - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().split("T")[0];
      const count = activityMap.get(iso) || 0;

      let level = 0;
      if (count > 0 && count <= 2) level = 1;
      else if (count >= 3 && count <= 4) level = 2;
      else if (count >= 5 && count <= 7) level = 3;
      else if (count >= 8) level = 4;

      cells.push({
        date: iso,
        count,
        level
      });
    }

    return cells;
  }, [activeFilter, liveStats.dynamicHeatmap]);

  const filteredStats = useMemo(() => {
    if (activeFilter === "All") {
      return {
        contributions: liveStats.contributions12m,
        activeDays: liveStats.activeDays,
        bestStreak: liveStats.bestStreak,
        contests: baselineData.contests
      };
    }
    const filtered = liveStats.dynamicHeatmap.filter(
      (item) => item.platform === activeFilter || item.platform === "All"
    );
    const contributions = filtered.reduce((acc, curr) => acc + (curr.count || 0), 0);
    const activeDays = filtered.length;
    return {
      contributions: contributions || (activeFilter === "TUF" ? liveStats.tufSolved : liveStats.leetCodeSolved),
      activeDays: activeDays || (activeFilter === "TUF" ? 12 : 15),
      bestStreak: activeFilter === "TUF" ? 7 : 11,
      contests: 0
    };
  }, [activeFilter, liveStats]);

  const months = ["Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov"];

  return (
    <Container id="dsa">
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          DSA & <span>Consistency</span>
        </Title>

        <Description
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Live real-time problem solving activity and consistency tracked dynamically across takeUforward and LeetCode.
        </Description>

        {/* Live Dynamic Sync Header */}
        <LiveSyncBar>
          <LiveStatus>
            <LiveDot />
            <span>
              <strong>Live Dynamic Sync:</strong>{" "}
              {lastSyncTime ? `Synced at ${lastSyncTime}` : "Fetching real-time stats..."}
            </span>
          </LiveStatus>

          <RefreshButton onClick={fetchLiveData} disabled={isRefreshing}>
            <RotateCw size={13} className={isRefreshing ? "spin" : ""} />
            <span>{isRefreshing ? "Syncing..." : "Refresh Live Stats"}</span>
          </RefreshButton>
        </LiveSyncBar>

        {/* High-level KPI Stats */}
        <StatsGrid>
          <StatCard
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
          >
            <StatIcon color="#38bdf8">
              <CheckCircle2 size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{liveStats.totalSolved}</StatValue>
              <StatLabel>Total Problems Solved</StatLabel>
            </StatInfo>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.08 }}
          >
            <StatIcon color="#f59e0b">
              <Flame size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{liveStats.bestStreak} Days</StatValue>
              <StatLabel>Best Streak</StatLabel>
            </StatInfo>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.16 }}
          >
            <StatIcon color="#10b981">
              <Calendar size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{liveStats.activeDays}</StatValue>
              <StatLabel>Active Days</StatLabel>
            </StatInfo>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.24 }}
          >
            <StatIcon color="#818cf8">
              <Layers size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{liveStats.contributions12m}</StatValue>
              <StatLabel>Contributions (12m)</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsGrid>

        {/* TakeUForward Consistency Heatmap Box */}
        <ConsistencyCard
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <ConsistencyHeader>
            <ConsistencyTitle>CONSISTENCY</ConsistencyTitle>
            <HeaderMetaRight>
              <PlatformBadge>
                {baselineData.platformsConnected} platforms connected
              </PlatformBadge>
              <DurationBadge>12 Months</DurationBadge>
            </HeaderMetaRight>
          </ConsistencyHeader>

          <FilterButtonGroup>
            <FilterPill
              active={activeFilter === "All"}
              onClick={() => setActiveFilter("All")}
            >
              All
            </FilterPill>
            <FilterPill
              active={activeFilter === "TUF"}
              onClick={() => setActiveFilter("TUF")}
            >
              <FaCode size={13} /> TUF
            </FilterPill>
            <FilterPill
              active={activeFilter === "LeetCode"}
              onClick={() => setActiveFilter("LeetCode")}
            >
              <SiLeetcode size={13} /> LeetCode
            </FilterPill>
          </FilterButtonGroup>

          <HeatmapScrollContainer>
            <HeatmapWrapper>
              <GridWeeks>
                {gridCells.map((cell, idx) => (
                  <Cell
                    key={idx}
                    level={cell.level}
                    title={`${cell.count} submissions on ${cell.date}`}
                  />
                ))}
              </GridWeeks>

              <MonthsRow>
                {months.map((m, idx) => (
                  <span key={idx}>{m}</span>
                ))}
              </MonthsRow>
            </HeatmapWrapper>
          </HeatmapScrollContainer>

          <HeatmapFooter>
            <BottomStatRow>
              <SubStat>
                <span className="num">{filteredStats.contributions}</span>
                <span className="label">Contributions • 12 mos</span>
              </SubStat>
              <SubStat>
                <span className="num">{filteredStats.activeDays}</span>
                <span className="label">Active days</span>
              </SubStat>
              <SubStat>
                <span className="num">{filteredStats.bestStreak}</span>
                <span className="label">Best streak</span>
              </SubStat>
              <SubStat>
                <span className="num">{filteredStats.contests}</span>
                <span className="label">Contests</span>
              </SubStat>
            </BottomStatRow>

            <LegendRow>
              <span>Less</span>
              <LegendCell bg="#161e2e" />
              <LegendCell bg="#1e3a8a" />
              <LegendCell bg="#2563eb" />
              <LegendCell bg="#38bdf8" />
              <LegendCell bg="#60a5fa" />
              <span>More</span>
            </LegendRow>
          </HeatmapFooter>
        </ConsistencyCard>

        {/* LeetCode & TUF Detailed Cards */}
        <PlatformsGrid>
          {/* LeetCode Card */}
          <PlatformCard>
            <PlatformHeader>
              <PlatformIdentity>
                <PlatformLogoBox color="#f59e0b">
                  <SiLeetcode size={22} />
                </PlatformLogoBox>
                <div>
                  <PlatformName>LeetCode</PlatformName>
                  <span style={{ fontSize: "12.5px", color: "#94a3b8" }}>
                    {liveStats.leetCodeSolved} Solved
                    {liveStats.ranking && ` • Rank #${liveStats.ranking.toLocaleString()}`}
                  </span>
                </div>
              </PlatformIdentity>
              <ExternalProfileLink
                href={baselineData.platforms.LeetCode.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Profile <ExternalLink size={12} />
              </ExternalProfileLink>
            </PlatformHeader>

            <ProgressBarsList>
              <CategoryProgress>
                <CategoryTop>
                  <span style={{ color: "#10b981" }}>Easy</span>
                  <span style={{ color: "#94a3b8" }}>
                    <strong style={{ color: "#f8fafc" }}>{liveStats.leetCodeEasy}</strong> / 975
                  </span>
                </CategoryTop>
                <ProgressBarTrack>
                  <ProgressBarFill
                    color="#10b981"
                    pct={Math.min(100, Math.round((liveStats.leetCodeEasy / 100) * 100))}
                  />
                </ProgressBarTrack>
              </CategoryProgress>

              <CategoryProgress>
                <CategoryTop>
                  <span style={{ color: "#f59e0b" }}>Medium</span>
                  <span style={{ color: "#94a3b8" }}>
                    <strong style={{ color: "#f8fafc" }}>{liveStats.leetCodeMedium}</strong> / 2138
                  </span>
                </CategoryTop>
                <ProgressBarTrack>
                  <ProgressBarFill
                    color="#f59e0b"
                    pct={Math.min(100, Math.round((liveStats.leetCodeMedium / 100) * 100))}
                  />
                </ProgressBarTrack>
              </CategoryProgress>

              <CategoryProgress>
                <CategoryTop>
                  <span style={{ color: "#ef4444" }}>Hard</span>
                  <span style={{ color: "#94a3b8" }}>
                    <strong style={{ color: "#f8fafc" }}>{liveStats.leetCodeHard}</strong> / 987
                  </span>
                </CategoryTop>
                <ProgressBarTrack>
                  <ProgressBarFill
                    color="#ef4444"
                    pct={Math.min(100, Math.round((liveStats.leetCodeHard / 50) * 100))}
                  />
                </ProgressBarTrack>
              </CategoryProgress>
            </ProgressBarsList>

            <TopTopicsWrapper>
              <TopicsHeading>Top Solved Topics</TopicsHeading>
              <TopicChips>
                {baselineData.platforms.LeetCode.topTopics.map((topic) => (
                  <TopicChip key={topic.label}>
                    {topic.label}
                    <span className="cnt">({topic.solved})</span>
                  </TopicChip>
                ))}
              </TopicChips>
            </TopTopicsWrapper>
          </PlatformCard>

          {/* takeUforward Card */}
          <PlatformCard>
            <PlatformHeader>
              <PlatformIdentity>
                <PlatformLogoBox color="#38bdf8">
                  <Code2 size={22} />
                </PlatformLogoBox>
                <div>
                  <PlatformName>takeUforward (TUF)</PlatformName>
                  <span style={{ fontSize: "12.5px", color: "#94a3b8" }}>
                    {liveStats.tufSolved} Solved
                  </span>
                </div>
              </PlatformIdentity>
              <ExternalProfileLink
                href={baselineData.platforms.TUF.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Profile <ExternalLink size={12} />
              </ExternalProfileLink>
            </PlatformHeader>

            <ProgressBarsList>
              {baselineData.platforms.TUF.categories.map((cat) => {
                const pct = Math.min(100, Math.round((cat.solved / 50) * 100));
                return (
                  <CategoryProgress key={cat.label}>
                    <CategoryTop>
                      <span style={{ color: cat.color }}>{cat.label}</span>
                      <span style={{ color: "#94a3b8" }}>
                        <strong style={{ color: "#f8fafc" }}>{cat.solved}</strong> / {cat.total}
                      </span>
                    </CategoryTop>
                    <ProgressBarTrack>
                      <ProgressBarFill color={cat.color} pct={pct} />
                    </ProgressBarTrack>
                  </CategoryProgress>
                );
              })}
            </ProgressBarsList>

            <TopTopicsWrapper>
              <TopicsHeading>Core DSA Topics</TopicsHeading>
              <TopicChips>
                {baselineData.platforms.TUF.topTopics.map((topic) => (
                  <TopicChip key={topic.label}>
                    {topic.label}
                    <span className="cnt">({topic.solved})</span>
                  </TopicChip>
                ))}
              </TopicChips>
            </TopTopicsWrapper>
          </PlatformCard>
        </PlatformsGrid>
      </ContentWrapper>
    </Container>
  );
};

export default CodingProfile;
